import api from './api';

const BASE_PATH = '/shared-settings';
const MAX_PAGE_SIZE = 100;

const fetchSharedSettings = async ({ page = null, limit = null } = {}) => {
  try {
    const params = {};
    if (page) params['page[number]'] = page;
    if (limit) params['page[size]'] = limit;
    const { data } = await api.get(BASE_PATH, { params });
    return { data: data.data, totalRecords: data.meta?.total_records };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

// Walks the paginated endpoint (page[size] is capped at 100 by the backend) and
// returns the full collection. For screens that need every record — dropdowns,
// lookups, archive impact — not for paginated list views.
const fetchAllSharedSettings = async () => {
  const pageParams = (page) => ({
    'page[number]': page,
    'page[size]': MAX_PAGE_SIZE,
  });
  try {
    const first = await api.get(BASE_PATH, { params: pageParams(1) });
    const firstRows = first.data.data || [];
    const totalRecords = first.data.meta?.total_records ?? firstRows.length;
    const totalPages = Math.ceil(totalRecords / MAX_PAGE_SIZE);

    const rest = await Promise.all(
      Array.from({ length: Math.max(totalPages - 1, 0) }, (_v, i) =>
        api.get(BASE_PATH, { params: pageParams(i + 2) }),
      ),
    );
    const all = rest.reduce(
      (acc, res) => acc.concat(res.data.data || []),
      firstRows,
    );
    return { data: all, totalRecords };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const fetchSharedSetting = async (id) => {
  try {
    const { data } = await api.get(`${BASE_PATH}/${id}`);
    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

// Locations of the group that no integration can enable as they stand. Walks the
// pages like fetchAllSharedSettings: grouping by integration is only truthful
// with every row, otherwise one integration splits across pages.
const fetchNeedsAttention = async (id) => {
  const path = `${BASE_PATH}/${id}/needs-attention`;
  const pageParams = (page) => ({
    'page[number]': page,
    'page[size]': MAX_PAGE_SIZE,
  });

  try {
    const first = await api.get(path, { params: pageParams(1) });
    const firstRows = first.data.data || [];
    const totalRecords = first.data.meta?.total_records ?? firstRows.length;
    const totalPages = Math.ceil(totalRecords / MAX_PAGE_SIZE);

    const rest = await Promise.all(
      Array.from({ length: Math.max(totalPages - 1, 0) }, (_v, i) =>
        api.get(path, { params: pageParams(i + 2) }),
      ),
    );

    return {
      data: rest.reduce(
        (acc, res) => acc.concat(res.data.data || []),
        firstRows,
      ),
      totalRecords,
      sharedSettingName: first.data.meta?.shared_setting_name,
    };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const createSharedSetting = async (attributes) => {
  try {
    const { data } = await api.post(BASE_PATH, {
      data: { type: 'shared_settings', attributes },
    });
    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const updateSharedSetting = async (id, attributes) => {
  try {
    const { data } = await api.put(`${BASE_PATH}/${id}`, {
      data: { type: 'shared_settings', attributes },
    });
    return { data: data.data };
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

const deleteSharedSetting = async (id) => {
  try {
    await api.delete(`${BASE_PATH}/${id}`, { data: {} });
    return {};
  } catch (e) {
    return { errors: e.response?.data?.errors };
  }
};

export default {
  fetchSharedSettings,
  fetchAllSharedSettings,
  fetchSharedSetting,
  fetchNeedsAttention,
  createSharedSetting,
  updateSharedSetting,
  deleteSharedSetting,
};
